/** The authenticated app shell: top bar, offline banner, routed page content, bottom nav. */
import { useEffect, useMemo } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';

import { useAuth } from '../auth/AuthContext';
import { BottomNav, TopBar, UserMenu } from '../components/ui';
import type { BottomNavItem, UserMenuItem } from '../components/ui';
import { firstAdminCommuneInsee } from '../features/admin/guard';
import { t } from '../i18n/fr';
import { OfflineBanner } from '../offline/OfflineBanner';
import { startOfflineSync } from '../offline/sync';

/**
 * The four tabs of the persistent bottom navigation
 * (`docs/maquettes/prototype-terrain.html`'s `.ui-bottomnav`): "Bilan" and
 * "Compte" have no single-screen target in this lot (the wrap-up screen
 * needs a session id, and the account editor already lives behind the top
 * bar's user menu) — "Bilan" reads as active on any session's summary
 * screen while still landing on "Démarrer" absent one, and "Compte" opens
 * the same profile editor as the user menu.
 */
const BOTTOM_NAV_ITEMS: BottomNavItem[] = [
  { key: 'start', label: t('session.nav.start'), to: '/' },
  { key: 'cases', label: t('session.nav.cases'), to: '/cases' },
  {
    key: 'summary',
    label: t('session.nav.summary'),
    to: '/',
    isActive: (pathname) => /^\/sessions\/[^/]+\/summary$/.test(pathname),
  },
  {
    key: 'account',
    label: t('session.nav.account'),
    to: '/onboarding?edit=1',
    isActive: (pathname) => pathname.startsWith('/onboarding'),
  },
];

/**
 * Render the persistent chrome around every screen behind
 * {@link RequireFullAccess}: `docs/AUTH.md`'s validated volunteer routes.
 *
 * The avatar opens the account menu, where the profile editor and signing
 * out are reachable from every page.
 *
 * @returns The shell element, with the matched child route in `<Outlet/>`.
 */
export function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const initials = user
    ? `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase() || undefined
    : undefined;

  // `docs/ADMIN.md` §1: "the nav entry does not render otherwise" — a
  // volunteer with no `commune_admin` membership never sees this item.
  const adminCommuneInsee = firstAdminCommuneInsee(user);

  const menuItems: UserMenuItem[] = useMemo(
    () => [
      { key: 'profile', label: t('userMenu.profile'), to: '/onboarding?edit=1' },
      ...(adminCommuneInsee
        ? [{ key: 'admin', label: t('userMenu.adminConsole'), to: `/admin/${adminCommuneInsee}` }]
        : []),
      {
        key: 'logout',
        label: t('userMenu.logout'),
        tone: 'danger',
        onSelect: () => {
          void logout().then(() => navigate('/login', { replace: true }));
        },
      },
    ],
    [adminCommuneInsee, logout, navigate],
  );

  // Idempotent: every mount tries, only the first one wires anything up
  // (`docs/OFFLINE.md` §2's triggers — `online`, app start, every 60s).
  useEffect(() => {
    startOfflineSync();
  }, []);

  return (
    <div className="app">
      <TopBar
        title={t('app.name')}
        userMenu={
          user ? (
            <UserMenu
              initials={initials ?? '?'}
              name={`${user.first_name} ${user.last_name}`.trim() || user.email}
              email={user.email}
              items={menuItems}
              buttonLabel={t('userMenu.open')}
            />
          ) : undefined
        }
      />
      <OfflineBanner />
      <main className="app__main">
        <Outlet />
      </main>
      <BottomNav items={BOTTOM_NAV_ITEMS} ariaLabel={t('session.nav.ariaLabel')} />
    </div>
  );
}
